import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';
import {
    searchBoardsRequest,
    createBoardRequest,
    deleteBoardRequest,
    resetBoardState
} from '../../store/boards/actions/board-actions';

const BoardSearchPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();

    const { boards, currentBoard, loading, createSuccess } = useSelector((state) => state.boards || {});
    const { value: boardsList = [], totalCount = 0, page: currentPage = 0, size: pageSize = 20, totalPages = 1 } = boards || {};
    const users = useSelector(state => state.user?.users || []);

    const [page, setPage] = useState(parseInt(urlSearchParams.get('page')) || 0);
    const [rowsPerPage, setRowsPerPage] = useState(parseInt(urlSearchParams.get('size')) || 20);
    const [searchParams, setSearchParams] = useState({
        name: urlSearchParams.get('name') || '',
    });
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        name: '',
        description: ''
    });

    const getUserFullName = (userId, users) => {
        if (!userId || !users) return 'N/A';
        const user = users.find(u => u.id === userId);
        if (user) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || user.email || 'N/A';
        }
        return 'N/A';
    };

    useEffect(() => {
        handleSearch();
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (createSuccess && currentBoard) {
            setShowCreateForm(false);
            setCreateFormData({ name: '', description: '' });
            dispatch(resetBoardState());
            navigate(`/boards/${currentBoard.id}`);
        }
    }, [createSuccess, currentBoard, navigate, dispatch]);

    const handleSearch = (e) => {
        if (e) {
            e.preventDefault();
        }

        const searchParamsObj = {
            name: searchParams.name || undefined,
            page: page,
            size: rowsPerPage,
        };

        Object.keys(searchParamsObj).forEach(key =>
            searchParamsObj[key] === undefined && delete searchParamsObj[key]
        );

        const newUrlParams = new URLSearchParams();
        if (searchParams.name) newUrlParams.set('name', searchParams.name);
        newUrlParams.set('page', page.toString());
        newUrlParams.set('size', rowsPerPage.toString());
        setUrlSearchParams(newUrlParams);

        dispatch(searchBoardsRequest(searchParamsObj));
    };

    const handleClearFilters = () => {
        setSearchParams({ name: '' });
        setPage(0);
        const newUrlParams = new URLSearchParams();
        newUrlParams.set('page', '0');
        newUrlParams.set('size', rowsPerPage.toString());
        setUrlSearchParams(newUrlParams);
        dispatch(searchBoardsRequest({ page: 0, size: rowsPerPage }));
    };

    const handleRowClick = (id) => {
        navigate(`/boards/${id}`);
    };

    const handleShowCreateForm = useCallback(() => {
        setShowCreateForm(true);
        setCreateFormData({ name: '', description: '' });
    }, []);

    const handleCreateFormChange = useCallback((field, value) => {
        setCreateFormData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const handleCreateBoard = useCallback(() => {
        if (!createFormData.name.trim()) {
            alert('Please fill in Board Name');
            return;
        }

        const payload = {
            name: createFormData.name.trim(),
            description: createFormData.description.trim()
        };

        dispatch(createBoardRequest(payload));
    }, [dispatch, createFormData]);

    const handleCancelCreate = useCallback(() => {
        setShowCreateForm(false);
        setCreateFormData({ name: '', description: '' });
    }, []);

    const handleDeleteBoard = useCallback((e, id) => {
        e.stopPropagation();
        if (window.confirm(t('common.confirmDelete'))) {
            dispatch(deleteBoardRequest(id));
        }
    }, [dispatch, t]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
        } catch (ex) {
            console.error(ex);
            return "";
        }
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h1 className="text-3xl font-bold text-[#34495e] mb-6">{t('board.title')}</h1>

                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('board.boardName')}</label>
                            <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                                value={searchParams.name}
                                onChange={(e) => setSearchParams({ ...searchParams, name: e.target.value })}
                                placeholder={t('board.enterBoardName')}
                            />
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={handleSearch}
                            className="flex items-center gap-2 bg-[#34495e] hover:bg-[#2c3e50] text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                            title={t('common.search')}
                        >
                            <MagnifyingGlassIcon className="h-5 w-5" />
                            {t('common.search')}
                        </button>
                        <button
                            onClick={handleClearFilters}
                            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                            title={t('common.clear')}
                        >
                            <XMarkIcon className="h-5 w-5" />
                            {t('common.clear')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    ID
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Description
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('board.totalTasks')}
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('task.status.open')}
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('task.status.inProgress')}
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('task.status.done')}
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('common.createdAt')}
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('common.createdBy')}
                                </th>
                                <th scope="col" className="relative px-6 py-3">
                                    <span className="sr-only">{t('common.actions')}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="10" className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#34495e] mb-4"></div>
                                            <p className="text-gray-500">{t('board.loadingBoards')}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : boardsList.length > 0 ? (
                                boardsList.map((board) => (
                                    <tr
                                        key={board.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleRowClick(board.id)}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {board.id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                            {board.name || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                            {board.description || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                            {board.totalTasks || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                {board.openTasks || 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                {board.inProgressTasks || 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                {board.doneTasks || 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(board.createdAt)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {getUserFullName(board.createdBy, users) || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={(e) => handleDeleteBoard(e, board.id)}
                                                className="text-red-600 hover:text-red-900"
                                                title="Delete Board"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="10" className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                            </svg>
                                            <p className="text-gray-500 font-medium">{t('board.noBoards')}</p>
                                            <p className="text-gray-400 text-sm mt-1">{t('board.tryAdjusting')}</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {boardsList.length > 0 && (
                    <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200">
                        <div className="text-sm text-gray-700">
                            Showing <span className="font-medium">{(currentPage * pageSize) + 1}</span> to{' '}
                            <span className="font-medium">
                                {Math.min((currentPage + 1) * pageSize, totalCount)}
                            </span>{' '}
                            of <span className="font-medium">{totalCount}</span> results
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 0}
                                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                                        page === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="sr-only">Previous</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                                    </svg>
                                </button>

                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i;
                                    } else if (page < 3) {
                                        pageNum = i;
                                    } else if (page > totalPages - 4) {
                                        pageNum = totalPages - 5 + i;
                                    } else {
                                        pageNum = page - 2 + i;
                                    }

                                    if (pageNum < 0 || pageNum >= totalPages) return null;

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setPage(pageNum)}
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                page === pageNum
                                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            {pageNum + 1}
                                        </button>
                                    );
                                })}

                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={page >= totalPages - 1}
                                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                                        page >= totalPages - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="sr-only">Next</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-6">
                {!showCreateForm ? (
                    <div className="flex gap-3">
                        <button
                            onClick={handleShowCreateForm}
                            disabled={loading}
                            className="bg-[#34495e] hover:bg-[#2c3e50] disabled:bg-gray-400 text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                            title={t('board.createNewBoard')}
                        >
                            {t('board.createBoard')}
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h3 className="text-xl font-semibold text-[#34495e] mb-4">{t('board.createNewBoard')}</h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('board.boardName')} *
                                </label>
                                <input
                                    type="text"
                                    value={createFormData.name}
                                    onChange={(e) => handleCreateFormChange('name', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                                    placeholder={t('board.enterBoardName')}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('common.description')}
                                </label>
                                <textarea
                                    value={createFormData.description}
                                    onChange={(e) => handleCreateFormChange('description', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                                    placeholder={t('board.enterDescription')}
                                    rows="3"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={handleCreateBoard}
                                disabled={loading || !createFormData.name.trim()}
                                className="bg-[#34495e] hover:bg-[#2c3e50] disabled:bg-gray-400 text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                            >
                                {loading ? t('common.creating') : t('common.create')}
                            </button>

                            <button
                                onClick={handleCancelCreate}
                                disabled={loading}
                                className="bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-800 font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BoardSearchPage;
